<script setup>
    import { ref, computed, onMounted } from 'vue';
    import { useToast } from '@/composables/use-toast';
    import { useLayoutComponents } from '@/composables/layout.js';
    import { useIcons } from '@/composables/icons.js';
    import { useStorage } from '@/composables/use-storage';
    import { useRouter } from 'vue-router';
    import LoaderWithText from '@/components/LoaderWithText.vue';
    import { fetchSellers, fetchSellerById } from '@/services/api';
    
    import * as EvalService from './services/importEvalService';
    
    const { addToast } = useToast();
    const { TextInput, SelectInput, InputError, ButtonLoading, Alert } = useLayoutComponents();
    const { User } = useIcons();
    const { userInfo } = useStorage();
    const router = useRouter();
    
    const datasetName = ref('');
    const selectedOwner = ref('');
    const ownersList = ref([]);
    const errors = ref({ datasetName: '', seller: '' });
    
    const isAdmin = computed(() => userInfo.value?.is_admin === true);
    const username = computed(() => {
        if (isAdmin.value && selectedOwner.value) {
            const owner = ownersList.value.find(o => o.value === selectedOwner.value);
            return owner ? owner.label : '';
        }
        return userInfo.value?.username || 'User';
    });
    const profilePic = computed(() => localStorage.getItem('profilePicture'));
    
    const taskFileSelected = ref(null);
    const taskFileContent = ref(null);
    const answerFileSelected = ref(null);
    const referenceFileSelected = ref(null);
    const referenceFileUploadedPath = ref(null);
    
    const referenceFileRequired = ref(false);
    const referenceFileNames = ref([]);
const isUploading = ref(false);
const evalFilesIsUploading = ref(false);
const evalFilesUploadProgress = ref(0);
const isSubmitting = ref(false);
const isDragging = ref(false);
    
    const buttonText = computed(() => 'Create Dataset');

    // Alert refs
    const showAlert = ref(false);
    const alertTitle = ref('');
    const alertMessage = ref('');
    const alertType = ref('info');
    const datasetId = ref(null);
    
    const isValidatingReferenceFile = ref(false);
    const validatingReferenceFileStep = ref(-1);
    const validateReferenceFileStepConfig = ref([
        {title: 'Initializing file validation', subtitle: 'Let\'s begin validating the content of your file'},
        {title: 'Updating file', subtitle: 'This may take a while depending on your connection'},
        {title: 'Validating file format', subtitle: 'Checking the file structure and ensuring all formats are compatible'}
    ]);
    
    
    onMounted(async () => {
        if (isAdmin.value) {
            fetchSellers().then((sellers) => {
                ownersList.value = sellers.filter(s => s).map(s => ({
                    value: s.user_id,
                    profilePicture: s.user?.profile_pic,
                    label: s.user?.username
                }));
                if (userInfo.value?.id) {
                    selectedOwner.value = userInfo.value?.id;
                }
            });
        } else if (userInfo.value.id) {
            await fetchSellerById(userInfo.value?.id).then((seller) => {
                selectedOwner.value = seller.user?.id;
            });
        }
    });
    
    const revalidateReferenceFile = async () => {
        if (!referenceFileSelected.value || !referenceFileUploadedPath.value) return;
        if (!Array.isArray(referenceFileNames.value) || referenceFileNames.value.length === 0) return;

        try {
            validatingReferenceFileStep.value = 2;
            isValidatingReferenceFile.value = true;
            const validation = await EvalService.validateReferenceFileContent(
                referenceFileUploadedPath.value,
                referenceFileNames.value
            );
            if (validation.error || (validation.data && !validation.data.valid)) {
                const errorMsg = validation.data?.error || validation.message || 'Invalid archive structure';
                throw new Error(errorMsg);
            }
            addToast('Reference file validated successfully', 'success');
        } catch (error) {
            addToast(error.message, 'error');
            removeReferenceFile();
        } finally {
            isValidatingReferenceFile.value = false;
            validatingReferenceFileStep.value = -1;
        }
    };

    const handleTaskFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
    
        try {
            const result = await EvalService.processTaskFile(file);
            
            taskFileContent.value = result.content;
            referenceFileRequired.value = result.referenceFileRequired;
            referenceFileNames.value = result.referenceFileNames || [];
            taskFileSelected.value = result.file;
            
            if (answerFileSelected.value) removeAnswerFileSelected();
            await revalidateReferenceFile();
    
        } catch (error) {
            addToast(error.message, 'error');
            event.target.value = '';
        }
    };
    
    const handleTaskFileDrop = async (event) => {
        event.preventDefault(); isDragging.value = false;
        if (evalFilesIsUploading.value || isUploading.value) return;
    
        const file = event.dataTransfer.files[0];
        if (file) {
            try {
                const result = await EvalService.processTaskFile(file);
                taskFileContent.value = result.content;
                referenceFileRequired.value = result.referenceFileRequired;
                referenceFileNames.value = result.referenceFileNames || [];
                taskFileSelected.value = result.file;
                if (answerFileSelected.value) removeAnswerFileSelected();
                await revalidateReferenceFile();
            } catch (error) {
                addToast(error.message, 'error');
            }
        }
    };
    
    const removeTaskFileSelected = () => {
        taskFileSelected.value = null;
        taskFileContent.value = null;
        referenceFileRequired.value = false;
        referenceFileNames.value = [];
        removeAnswerFileSelected();
        const el = document.getElementById('taskFileInput');
        if(el) el.value = '';
    };
    
    const handleAnswerFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
    
        try {
            const result = await EvalService.processAnswerFile(file, taskFileContent.value);
            answerFileSelected.value = result.file;
        } catch (error) {
            addToast(error.message, 'error');
            event.target.value = '';
        }
    };
    
    const handleAnswerFileDrop = async (event) => {
        event.preventDefault(); isDragging.value = false;
        if (evalFilesIsUploading.value || isUploading.value) return;
    
        const file = event.dataTransfer.files[0];
        if (file) {
            try {
                const result = await EvalService.processAnswerFile(file, taskFileContent.value);
                answerFileSelected.value = result.file;
            } catch (error) {
                addToast(error.message, 'error');
            }
        }
    };
    
    const removeAnswerFileSelected = () => {
        answerFileSelected.value = null;
        const el = document.getElementById('answerFileInput');
        if (el) el.value = '';
    };
    
    const handleReferenceFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
    
        try {
            const result = await EvalService.processReferenceFile(
                file, 
                selectedOwner.value || userInfo.value?.id, 
                (progress) => { evalFilesUploadProgress.value = progress; },
                (step) => { 
                    validatingReferenceFileStep.value = step;
                    isValidatingReferenceFile.value = step >= 0;
                },
                referenceFileNames.value
            );
    
            referenceFileSelected.value = result.file;
            referenceFileUploadedPath.value = result.uploadedPath;
            addToast('Reference file validated successfully', 'success');
    
        } catch (error) {
            addToast(error.message, 'error');
            event.target.value = '';
            referenceFileSelected.value = null;
        }
    };
    
    const handleReferenceFileDrop = async (event) => {
        event.preventDefault(); isDragging.value = false;
        if (evalFilesIsUploading.value || isUploading.value) return;
    
        const file = event.dataTransfer.files[0];
        if (file) {
            try {
                const result = await EvalService.processReferenceFile(
                    file,
                    selectedOwner.value || userInfo.value?.id,
                    (progress) => { evalFilesUploadProgress.value = progress; },
                    (step) => { 
                        validatingReferenceFileStep.value = step;
                        isValidatingReferenceFile.value = step >= 0;
                    },
                    referenceFileNames.value
                );
                referenceFileSelected.value = result.file;
                referenceFileUploadedPath.value = result.uploadedPath;
                addToast('Reference file validated successfully', 'success');
            } catch (error) {
                addToast(error.message, 'error');
            }
        }
    };
    
    const removeReferenceFile = () => {
        referenceFileSelected.value = null;
        referenceFileUploadedPath.value = null;
        const el = document.getElementById('referenceFileInput');
        if(el) el.value = '';
    };
    
    
    const submitDataset = async () => {
        if (!datasetName.value) {
            errors.value.datasetName = 'Name is required';
            addToast('Please name your dataset', 'error');
            return;
        }
        if (!taskFileSelected.value || !answerFileSelected.value) {
            addToast('Please upload required files (Tasks and Answers)', 'error');
            return;
        }
        if (referenceFileRequired.value && !referenceFileSelected.value) {
            addToast('Reference file is required for this task list', 'error');
            return;
        }
    
        isSubmitting.value = true;
        isUploading.value = true;
    
        try {
            const response = await EvalService.submitEvalDataset({
                datasetName: datasetName.value,
                description: '',
                ownerId: selectedOwner.value || userInfo.value?.id,
                taskFile: taskFileSelected.value,
                answerFile: answerFileSelected.value,
                referenceFileObj: referenceFileSelected.value ? {
                    path: referenceFileUploadedPath.value,
                    name: referenceFileSelected.value.name
                } : null,
                onProgress: (type, progress) => {
                    evalFilesUploadProgress.value = progress;
                }
            });

            if (response.status === 200 || response.status === 201) {
                datasetId.value = response.data?.id;
                showAlert.value = true;
                alertTitle.value = 'Dataset created successfully!';
                alertMessage.value = 'Would you like to create a listing for this dataset now?';
                alertType.value = 'success';
            } else {
                throw new Error(response.message || 'Failed to create dataset');
            }
    
        } catch (error) {
            addToast(error.message || 'Error creating dataset', 'error');
        } finally {
            isSubmitting.value = false;
            isUploading.value = false;
        }
    };
    
    const handleAlertConfirm = () => {
        if (!datasetId.value) {
            addToast('Dataset ID not available', 'error');
            return;
        }
        
        router.push({ 
            name: 'dataset-listing-create', 
            params: { 
                datasetId: datasetId.value.toString()
            },
            query: {
                isEvalDataset: 'true'
            }
        });
    };

    const handleAlertCancel = () => {
        showAlert.value = false;
        router.push('/creator-profile?page=eval-datasets&subpage=eval-datasets');
    };

    const showEvalFormatModal = ref(false);
    const redirectToDocumentation = () => { window.open('https://docs.portexai.com/portex-docs', '_blank'); };
    const formatFileSize = EvalService.formatFileSize;
    
    const handleEvalDragLeave = (event) => {
        event.preventDefault(); isDragging.value = false;
    };
    const handleEvalDragOver = (event) => {
        event.preventDefault();
        if (!evalFilesIsUploading.value && !isUploading.value) isDragging.value = true;
    };
    </script>
    
    <template>
        <Alert 
            v-if="showAlert" 
            :show="showAlert" 
            :title="alertTitle" 
            :message="alertMessage" 
            :type="alertType" 
            confirmText="Yes, create listing"
            cancelText="No, back to Data Studio"
            @confirm="handleAlertConfirm" 
            @close="handleAlertCancel"
            :showCloseBtn="false"
            dataTestId="eval-dataset-create-listing-alert"
        />
        <div class="h-full flex py-8 px-4 overflow-y-auto">
            <div class="flex flex-col items-center justify-start mx-auto ">
                <div class="w-full flex flex-col items-center justify-center mb-10 gap-1">
                    <h1 class="text-2xl 2xl:text-3xl font-bold bg-gradient-to-r from-[#FFFFFF] to-[#999999] bg-clip-text text-transparent w-fit">Import & Upload Eval</h1>
                    <p class="text-sm 2xl:text-base text-white/80 text-center">
                        Import currently supports <span class="font-semibold text-green-300">Q&A (single-turn)</span> eval format only.
                        Please reference our <button class="underline text-echoes-primary hover:text-echoes-primary/80" @click="redirectToDocumentation">documentation</button> for the required schema.
                    </p>
                </div>

                <div class="w-full mb-6 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3">
                    <div class="flex items-start gap-2 text-sm 2xl:text-base text-white/85">
                        <i class="uil uil-info-circle text-amber-300 mt-0.5"></i>
                        <p class="m-0">
                            Agentic eval import is not available yet. To create an agentic eval, use
                            <span class="font-semibold text-white">Eval Builder</span> and choose
                            <span class="font-semibold text-white">Agentic</span>.
                        </p>
                    </div>
                </div>
    
                <div class="w-full">
                    <div class="space-y-6 flex flex-col h-full">
                        <div class="flex flex-col sm:flex-row">
                            <div class="text-base w-1/2 font-medium text-white flex justify-center flex-col h-full">
                                <div class="mb-1">
                                    <div class="flex items-center w-fit gap-2">
                                        <div class="text-sm">
                                            Owner
                                        </div>
                                    </div>
                                    <InputError :message="errors.seller" class="text-sm text-red-500" />
                                </div>
                                <div class="flex items-center w-full">
                                    <template v-if="isAdmin">
                                        <div class="w-full whitespace-nowrap flex-1">
                                    <SelectInput 
                                        id="owner" 
                                        v-model="selectedOwner" 
                                        :options="ownersList"
                                        placeholder="Select owner"
                                        class="w-full"
                                        :showProfilePicture="true"
                                        :disabled="isUploading || isSubmitting"
                                        dataTestId="eval-owner-select"
                                    />
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div class="flex w-full items-center gap-2 px-4 py-2 2xl:px-5 text-base rounded-2xl bg-echoes-background-gray/30 text-white border border-echoes-border-gray placeholder-white/70">
                                            <div class="w-6 h-6 rounded-full overflow-hidden bg-echoes-primary">
                                                <img v-if="profilePic" :src="profilePic" alt="User avatar" class="w-full h-full object-cover" />
                                                <User v-else class="w-full h-full p-1 text-white/80" />
                                            </div>
                                            <div class="whitespace-nowrap">{{ username }}</div>
                                        </div>
                                    </template>
                                    <div class="mx-2 text-white/80 text-3xl self-center flex-shrink-0 hidden lg:block">/</div>
                                </div>
                            </div>
                            
                            <div class="text-base w-1/2 font-medium text-white flex justify-center flex-col h-full">
                                <div class="mb-1">
                                    <div class="flex items-center w-fit gap-2 whitespace-nowrap">
                                        <div class="text-sm">Eval Dataset Name</div>
                                        <InputError :message="errors.datasetName" class="text-sm text-red-500" />
                                    </div>
                                </div>
                                
                            <TextInput    
                                id="datasetName" 
                                v-model="datasetName" 
                                placeholder="Insert the dataset's name"
                                :required="true" 
                                class="w-full" 
                                :disabled="isUploading || isSubmitting"
                                dataTestId="eval-dataset-name-input"
                            />
                            </div>
                        </div>
                    </div>
                </div>
    
                <div class="w-full">
                    <div class="flex flex-col h-full mt-6">
                        <div class="space-y-6 mt-2">
                        <div class="flex flex-col">
                            <div class="mb-2">
                                <div class="flex items-center gap-2">
                                    <span class="font-medium text-white">1.</span>
                                    <span class="font-medium text-white text-sm">Task List</span>
                                    <div class="text-echoes-primary hover:text-echoes-primary/80 transition-colors relative group">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                            class="w-5 h-5 cursor-help flex-shrink-0">
                                            <path fill="currentColor"
                                                d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                                            </path>
                                        </svg>
                                        <span class="absolute left-[calc(50%+1.5rem)] -translate-x-1/2 bottom-full mb-2 px-3 py-2 text-sm text-white/80 bg-echoes-background-gray rounded-lg whitespace-normal w-max max-w-[300px] border-0 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 ease-in-out z-50 pointer-events-none text-white">JSON file containing your tasks. Each record must contain a task_id, task_prompt, and reference_file (null or empty string if not applicable).</span>
                                    </div>
                                </div>
                            </div>
                        <div class="relative border-2 border-dashed rounded-lg bg-echoes-background-black transition-all duration-200 p-4" 
                            :class="{ 
                                'cursor-not-allowed opacity-50 pointer-events-none': evalFilesIsUploading || isUploading,
                                'border-echoes-primary': isDragging && !evalFilesIsUploading && !isUploading,
                                'border-echoes-border-gray': !isDragging || evalFilesIsUploading || isUploading
                            }"
                            @dragleave="handleEvalDragLeave"
                            @dragover="handleEvalDragOver" 
                            @drop="handleTaskFileDrop"
                            @dragenter.prevent>
                            
                            <div class="flex items-center gap-4" v-if="!taskFileSelected">
                                    <div class="flex items-center gap-2 px-2 py-1 border-2 border-echoes-background-gray text-white rounded-lg transition cursor-pointer text-sm">
                                        <svg class="w-5 h-5" viewBox="0 0 94 94" 
                                            fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M15.6667 58.7501L15.6667 66.5834C15.6667 73.0728 20.9274 78.3334 27.4167 78.3334L66.5834 78.3334C73.0728 78.3334 78.3334 73.0728 78.3334 66.5834V58.7501M47.0001 15.6667L47.0001 62.6667M47.0001 15.6667L62.6667 31.3334M47.0001 15.6667L31.3334 31.3334"
                                                stroke="#A7ACB9" stroke-width="6" stroke-linecap="round"
                                                stroke-linejoin="round" />
                                        </svg>
                                        <span>Select File</span>
                                    </div>
                                    <div class="flex-1">
                                        <p class="text-sm text-white/80">Drop your task list file here, or click on Select File</p>
                                    </div>
                                </div>
                            <input type="file" id="taskFileInput" @change="handleTaskFileSelect" class="hidden" :disabled="evalFilesIsUploading || isUploading || isSubmitting" accept=".json,.txt" data-testid="task-file-input" />
                            <label for="taskFileInput" class="absolute inset-0 cursor-pointer" :class="{ 'cursor-not-allowed pointer-events-none': evalFilesIsUploading || isUploading || isSubmitting || taskFileSelected }" v-if="!taskFileSelected" aria-label="Select task file" data-testid="task-file-label"></label>
                            
                            <div v-if="taskFileSelected" class="flex items-center justify-between p-3">
                                <div class="flex flex-col">
                                    <div class="text-white font-medium text-base">{{ taskFileSelected.name }}</div>
                                    <div class="text-white/50 text-sm">{{ formatFileSize(taskFileSelected.size) }}</div>
                                </div>
                                <button @click="removeTaskFileSelected" class="text-sm text-red-500 hover:text-red-600 transition-colors underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" :disabled="isSubmitting" data-testid="remove-task-file-button">Remove</button>
                            </div>
                            </div>
                        </div>
    
                        <div class="flex flex-col">
                            <div class="mb-2">
                                <div class="flex items-center gap-2">
                                    <span class="font-medium text-white">2.</span>
                                    <span class="font-medium text-white text-sm">Answer Keys</span>
                                    <div class="text-echoes-primary hover:text-echoes-primary/80 transition-colors relative group">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                            class="w-5 h-5 cursor-help flex-shrink-0">
                                            <path fill="currentColor"
                                                d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                                            </path>
                                        </svg>
                                        <span class="absolute left-1/2 text-white -translate-x-1/2 bottom-full mb-2 px-3 py-2 text-sm text-white/80 bg-echoes-background-gray rounded-lg whitespace-normal w-max max-w-[300px] border-0 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 ease-in-out z-50 pointer-events-none">JSON file mapping each task_id to its answer. Must include task_id and answer fields. Always hidden unless included in your Core Dataset.</span>
                                    </div>
                                </div>
                            </div>
                        <div class="relative border-2 border-dashed rounded-lg bg-echoes-background-black transition-all duration-200 p-4" 
                            :class="{ 
                                'cursor-not-allowed opacity-50 pointer-events-none': evalFilesIsUploading || isUploading,
                                'border-echoes-primary': isDragging && !evalFilesIsUploading && !isUploading,
                                'border-echoes-border-gray': !isDragging || evalFilesIsUploading || isUploading
                            }"
                            @dragleave="handleEvalDragLeave"
                            @dragover="handleEvalDragOver" 
                            @drop="handleAnswerFileDrop"
                            @dragenter.prevent>
                            
                            <div class="flex items-center gap-4" v-if="!answerFileSelected">
                                    <div class="flex items-center gap-2 px-2 py-1 border-2 border-echoes-background-gray text-white rounded-lg transition cursor-pointer text-sm">
                                        <svg class="w-5 h-5" viewBox="0 0 94 94" 
                                            fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M15.6667 58.7501L15.6667 66.5834C15.6667 73.0728 20.9274 78.3334 27.4167 78.3334L66.5834 78.3334C73.0728 78.3334 78.3334 73.0728 78.3334 66.5834V58.7501M47.0001 15.6667L47.0001 62.6667M47.0001 15.6667L62.6667 31.3334M47.0001 15.6667L31.3334 31.3334"
                                                stroke="#A7ACB9" stroke-width="6" stroke-linecap="round"
                                                stroke-linejoin="round" />
                                        </svg>
                                        <span>Select File</span>
                                    </div>
                                    <div class="flex-1">
                                        <p class="text-sm text-white/80">Drop your answer key file here, or click on Select File</p>
                                    </div>
                                </div>
                            <input type="file" id="answerFileInput" @change="handleAnswerFileSelect" class="hidden" :disabled="evalFilesIsUploading || isUploading || isSubmitting" accept=".json,.txt" data-testid="answer-file-input" />
                            <label for="answerFileInput" class="absolute inset-0 cursor-pointer" :class="{ 'cursor-not-allowed pointer-events-none': evalFilesIsUploading || isUploading || isSubmitting || answerFileSelected }" v-if="!answerFileSelected" aria-label="Select answer file" data-testid="answer-file-label"></label>
                            
                            <div v-if="answerFileSelected" class="flex items-center justify-between p-3">
                                <div class="flex flex-col">
                                    <div class="text-white font-medium text-base">{{ answerFileSelected.name }}</div>
                                    <div class="text-white/50 text-sm">{{ formatFileSize(answerFileSelected.size) }}</div>
                                </div>
                                <button @click="removeAnswerFileSelected" class="text-sm text-red-500 hover:text-red-600 transition-colors underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" :disabled="isSubmitting" data-testid="remove-answer-file-button">Remove</button>
                            </div>
                            </div>
                        </div>
    
                        <div class="flex flex-col">
                            <div class="mb-2">
                                <div class="flex items-center gap-2">
                                    <span class="font-medium text-white">3.</span>
                                    <span class="font-medium text-white text-sm">Reference Files {{ referenceFileRequired ? '(Required)' : '(Optional)' }}</span>
                                    <div class="text-echoes-primary hover:text-echoes-primary/80 transition-colors relative group">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                            class="w-5 h-5 cursor-help flex-shrink-0">
                                            <path fill="currentColor"
                                                d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                                            </path>
                                        </svg>
                                        <span class="absolute left-1/2 text-white -translate-x-1/2 bottom-full mb-2 px-3 py-2 text-sm text-white/80 bg-echoes-background-gray rounded-lg whitespace-normal w-max max-w-[300px] border-0 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 ease-in-out z-50 pointer-events-none">Upload a <strong>.zip or .tar.gz</strong> archive containing your reference text or image files.All files must be at the top level <strong>(no folders)</strong>.For tar, run from inside your refs folder. <br> Example: <code>cd refs && tar -czf ../refs.tar.gz *</code></span>
                                    </div>
                                </div>
                            </div>
                        <div class="relative border-2 border-dashed rounded-lg bg-echoes-background-black transition-all duration-200 p-4" 
                            :class="{ 
                                'cursor-not-allowed opacity-50 pointer-events-none': evalFilesIsUploading || isUploading,
                                'border-echoes-primary': isDragging && !evalFilesIsUploading && !isUploading,
                                'border-echoes-border-gray': !isDragging || evalFilesIsUploading || isUploading
                            }"
                            @dragleave="handleEvalDragLeave"
                            @dragover="handleEvalDragOver" 
                            @drop="handleReferenceFileDrop"
                            @dragenter.prevent>
                            
                            <div class="flex items-center gap-4" v-if="!referenceFileSelected && !isValidatingReferenceFile">
                                    <div class="flex items-center gap-2 px-2 py-1 border-2 border-echoes-background-gray text-white rounded-lg transition cursor-pointer text-sm">
                                        <svg class="w-5 h-5" viewBox="0 0 94 94" 
                                            fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M15.6667 58.7501L15.6667 66.5834C15.6667 73.0728 20.9274 78.3334 27.4167 78.3334L66.5834 78.3334C73.0728 78.3334 78.3334 73.0728 78.3334 66.5834V58.7501M47.0001 15.6667L47.0001 62.6667M47.0001 15.6667L62.6667 31.3334M47.0001 15.6667L31.3334 31.3334"
                                                stroke="#A7ACB9" stroke-width="6" stroke-linecap="round"
                                                stroke-linejoin="round" />
                                        </svg>
                                        <span>Select File</span>
                                    </div>
                                    <div class="flex-1">
                                        <p class="text-sm text-white/80">Drop your reference file here, or click on Select File</p>
                                    </div>
                                </div>
                            <input type="file" id="referenceFileInput" @change="handleReferenceFileSelect" class="hidden" :disabled="evalFilesIsUploading || isUploading || isSubmitting || isValidatingReferenceFile" accept=".zip,.tar.gz" data-testid="reference-file-input" />
                            <label for="referenceFileInput" class="absolute inset-0 cursor-pointer" :class="{ 'cursor-not-allowed pointer-events-none': evalFilesIsUploading || isUploading || isSubmitting || referenceFileSelected }" v-if="!referenceFileSelected" aria-label="Select reference file" data-testid="reference-file-label"></label>
                            <LoaderWithText v-if="isValidatingReferenceFile"
                                :alignment="'inline'"
                                :current-step="validatingReferenceFileStep"
                                :steps-config="validateReferenceFileStepConfig"
                             />
                            
                            <div v-if="referenceFileSelected" class="flex items-center justify-between p-3">
                                <div class="flex flex-col">
                                    <div class="text-white font-medium text-base">{{ referenceFileSelected.name }}</div>
                                    <div class="text-white/50 text-sm">{{ formatFileSize(referenceFileSelected.size) }}</div>
                                </div>
                                <button @click="removeReferenceFile" class="text-sm text-red-500 hover:text-red-600 transition-colors underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" :disabled="isSubmitting" data-testid="remove-reference-file-button">Remove</button>
                            </div>
                            </div>
                        </div>
    
                        <div v-if="evalFilesIsUploading || isUploading" class="mt-4 p-4 bg-echoes-background-gray/30 rounded-md">
                            <div class="relative w-full h-2.5 bg-echoes-background-gray/30 rounded-full overflow-hidden border border-echoes-border-gray shadow-inner">
                                <div class="h-full rounded-full transition-all duration-300 ease-out" :style="{ width: `${evalFilesUploadProgress}%`, background: 'linear-gradient(90deg, #FE3E01 0%, #FE3E01 100%)', boxShadow: '0 0 10px #FE3E01' }"></div>
                            </div>
                            <div class="text-sm text-white/80 mt-1 font-medium text-center">{{ Math.round(evalFilesUploadProgress) }}%</div>
                        </div>
                    </div>
    
                    <div class="action-container">
                        <div class="action-buttons">
                        <button 
                            class="create-dataset-btn hidden sm:block" 
                            data-testid="dataset-submit-button"
                            @click="submitDataset" 
                            :disabled="isSubmitting || isUploading || isValidatingReferenceFile"
                        >
                            <ButtonLoading 
                                v-if="isSubmitting" 
                                :text="isUploading ? 'Uploading...' : 'Creating...'" 
                                size="sm" 
                            />
                            <span v-else>{{ buttonText }}</span>
                        </button>
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </div>
    </template>
    
    <style scoped>
    .action-container {
        @apply flex justify-center md:justify-end;
    }
    
    .action-buttons {
        @apply flex gap-4 items-center;
    }
    
    .create-dataset-btn {
        @apply px-6 2xl:px-8 py-2 my-6 text-sm bg-gradient-to-br from-green-500 to-green-700 text-white font-semibold rounded-full hover:bg-gradient-to-br hover:from-green-600 hover:to-green-800 transition duration-200 flex items-center justify-center min-w-[160px] disabled:opacity-70 disabled:cursor-not-allowed disabled:from-green-500/60 disabled:to-green-700/60;
    }
    </style>
    
